/**
 * AGANTUK FLORAL CRAFT - THEME JS
 * Ajax Cart Drawer, Wishlist, Quick View, and Interactive Bouquet Builder
 */

document.addEventListener('DOMContentLoaded', () => {
  initCartDrawer();
  initWishlist();
  initBouquetBuilder();
  initQuickView();
  initNewArrivalsCarousel();
  initBestsellersCarousel();
  initScrollDrivenCarousels();
  initFloralBloomHeadings();
  initInstantPagePrefetch();
});

// Cart Drawer Logic & Persistence
const CART_STORAGE_KEY = 'agantuk_cart_items_v2';
let cartItems = [];

try {
  const savedCart = localStorage.getItem(CART_STORAGE_KEY);
  if (savedCart) {
    cartItems = JSON.parse(savedCart);
  }
} catch (e) {
  cartItems = [];
}

function saveCartToStorage() {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
  } catch (e) {
    console.warn('Unable to persist cart to storage', e);
  }
}

function initCartDrawer() {
  updateCartBadge();

  const cartToggleBtns = document.querySelectorAll('.cart-toggle');
  const cartOverlay = document.getElementById('cartOverlay');
  const cartDrawer = document.getElementById('cartDrawer');
  const closeCartBtn = document.getElementById('closeCartBtn');

  if (!cartOverlay || !cartDrawer) return;

  function openCart() {
    cartOverlay.classList.add('open');
    cartDrawer.classList.add('open');
    renderCart();
  }

  function closeCart() {
    cartOverlay.classList.remove('open');
    cartDrawer.classList.remove('open');
  }

  window.CartDrawer = {
    open: openCart,
    close: closeCart,
    addItem: (item) => {
      addToCart(item);
      openCart();
    }
  };

  cartToggleBtns.forEach(btn => btn.addEventListener('click', openCart));
  if (closeCartBtn) closeCartBtn.addEventListener('click', closeCart);
  if (cartOverlay) cartOverlay.addEventListener('click', closeCart);

  // Close on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && cartDrawer.classList.contains('open')) {
      closeCart();
    }
  });

  // Global add to cart listener
  document.body.addEventListener('click', (e) => {
    if (e.target.closest('.add-to-cart-btn')) {
      const btn = e.target.closest('.add-to-cart-btn');
      if (btn.disabled || btn.classList.contains('is-disabled')) return;

      const id = parseInt(btn.dataset.id || "1", 10);
      const title = btn.dataset.title || "Handcrafted Crochet Item";
      const price = parseInt(btn.dataset.price || "899", 10);
      const cardImg = btn.closest('.product-card')?.querySelector('.primary-image')?.src;
      const mainImg = document.querySelector('.main-image-display')?.src || document.querySelector('.product-thumb img')?.src;
      const image = btn.dataset.image || cardImg || mainImg || '';

      addToCart({ id, title, price, image, quantity: 1, variant: "Handmade Standard" });
      openCart();
    }
  });
}

function addToCart(item) {
  const existing = cartItems.find(i => i.title === item.title);
  if (existing) {
    existing.quantity += item.quantity || 1;
  } else {
    cartItems.push(item);
  }
  saveCartToStorage();
  updateCartBadge();

  // Background sync with Shopify Cart API if valid ID
  const numId = parseInt(item.id, 10);
  if (!isNaN(numId) && numId > 100) {
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: numId, quantity: item.quantity || 1 })
    }).catch(() => {});
  }
}

function renderCart() {
  const body = document.getElementById('cartDrawerItems');
  const subtotalEl = document.getElementById('cartSubtotal');
  const progressEl = document.getElementById('shippingProgress');

  if (!body) return;

  if (cartItems.length === 0) {
    body.innerHTML = `
      <div class="cart-empty-state">
        <div class="cart-empty-icon-wrap">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#D48C46" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <path d="M16 10a4 4 0 0 1-8 0"></path>
          </svg>
        </div>
        <h4 class="cart-empty-heading">Your Bag is Empty</h4>
        <p class="cart-empty-subtext">Discover our slow-crafted crochet blooms and bespoke floral bouquets.</p>
        <button class="cart-empty-btn" onclick="document.getElementById('cartOverlay').click()">
          Explore Handcrafted Blooms
        </button>
      </div>
    `;
    if (subtotalEl) subtotalEl.textContent = '₹0';
    if (progressEl) progressEl.style.width = '0%';
    const shipText = document.getElementById('shippingMsg');
    if (shipText) {
      shipText.innerHTML = `
        <svg class="shipping-truck-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <rect x="1" y="3" width="15" height="13"></rect>
          <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
          <circle cx="5.5" cy="18.5" r="2.5"></circle>
          <circle cx="18.5" cy="18.5" r="2.5"></circle>
        </svg>
        <span>Add <strong>₹1,499</strong> more for <strong>FREE Shipping</strong></span>
      `;
    }
    updateCartBadge();
    return;
  }

  let subtotal = 0;
  body.innerHTML = cartItems.map((item, index) => {
    subtotal += item.price * item.quantity;
    const lineTotal = item.price * item.quantity;
    return `
      <div class="cart-item">
        <div class="cart-item-image-wrapper">
          <img src="${item.image || '/assets/hero-crochet-bouquet.png'}" alt="${item.title}" loading="lazy">
        </div>
        <div class="cart-item-details">
          <div class="cart-item-top">
            <h4 class="cart-item-title">${item.title}</h4>
            <button class="cart-item-remove" onclick="removeCartItem(${index})" title="Remove item" aria-label="Remove item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
            </button>
          </div>
          ${item.variant ? `<div class="cart-item-variant">${item.variant}</div>` : ''}
          <div class="cart-item-bottom">
            <div class="qty-control">
              <button class="qty-btn" onclick="updateQty(${index}, -1)" aria-label="Decrease quantity">−</button>
              <span class="qty-num">${item.quantity}</span>
              <button class="qty-btn" onclick="updateQty(${index}, 1)" aria-label="Increase quantity">+</button>
            </div>
            <div class="cart-item-price-group">
              <span class="cart-item-price">₹${lineTotal.toLocaleString('en-IN')}</span>
              ${item.quantity > 1 ? `<span class="cart-item-unit-price">(₹${item.price.toLocaleString('en-IN')} ea)</span>` : ''}
            </div>
          </div>
        </div>
      </div>
    `;
  }).join('');

  if (subtotalEl) subtotalEl.textContent = `₹${subtotal.toLocaleString('en-IN')}`;
  
  // Calculate free shipping threshold (₹1,499)
  const freeThreshold = 1499;
  const pct = Math.min(100, Math.round((subtotal / freeThreshold) * 100));
  if (progressEl) progressEl.style.width = `${pct}%`;
  const shipText = document.getElementById('shippingMsg');
  if (shipText) {
    if (subtotal >= freeThreshold) {
      shipText.innerHTML = `
        <svg class="shipping-truck-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#246644" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
        <span style="color: #246644; font-weight: 600;">You've unlocked FREE Shipping!</span>
      `;
    } else {
      const diff = freeThreshold - subtotal;
      shipText.innerHTML = `
        <svg class="shipping-truck-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <rect x="1" y="3" width="15" height="13"></rect>
          <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
          <circle cx="5.5" cy="18.5" r="2.5"></circle>
          <circle cx="18.5" cy="18.5" r="2.5"></circle>
        </svg>
        <span>Add <strong>₹${diff.toLocaleString('en-IN')}</strong> more for <strong>FREE Shipping</strong></span>
      `;
    }
  }

  updateCartBadge();
}

window.updateQty = function(index, delta) {
  if (!cartItems[index]) return;
  cartItems[index].quantity += delta;
  if (cartItems[index].quantity <= 0) {
    cartItems.splice(index, 1);
  }
  saveCartToStorage();
  renderCart();
};

window.removeCartItem = function(index) {
  cartItems.splice(index, 1);
  saveCartToStorage();
  renderCart();
};

function updateCartBadge() {
  const totalCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const badges = document.querySelectorAll('.cart-count-badge');
  badges.forEach(b => b.textContent = totalCount);
}

// Proceed to Checkout handler
window.proceedToCheckout = async function() {
  if (!cartItems || cartItems.length === 0) {
    alert("Your shopping bag is empty. Please add some handcrafted blooms to checkout!");
    return;
  }

  const checkoutBtn = document.getElementById('cartCheckoutBtn');
  if (checkoutBtn) {
    checkoutBtn.disabled = true;
    checkoutBtn.style.opacity = '0.8';
    checkoutBtn.style.cursor = 'wait';
    const label = checkoutBtn.querySelector('.checkout-btn-label') || checkoutBtn.querySelector('span');
    if (label) label.textContent = 'PROCEEDING TO CHECKOUT...';
  }

  try {
    const itemsToAdd = cartItems
      .map(item => ({
        id: parseInt(item.id, 10),
        quantity: parseInt(item.quantity, 10) || 1
      }))
      .filter(i => !isNaN(i.id) && i.id > 100);

    if (itemsToAdd.length > 0) {
      await fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } }).catch(() => {});
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: itemsToAdd })
      }).catch(() => {});
    }
  } catch (e) {
    console.warn('Checkout sync notice:', e);
  }

  // Redirect to Shopify Checkout
  window.location.href = '/checkout';
};

// Wishlist Logic with LocalStorage Persistence & Sync
function initWishlist() {
  const STORAGE_KEY = 'agantuk_wishlist_items_v2';
  let wishlistItems = [];

  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      wishlistItems = JSON.parse(saved);
      if (!Array.isArray(wishlistItems)) wishlistItems = [];
    }
  } catch (e) {
    wishlistItems = [];
  }

  function saveWishlistToStorage() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(wishlistItems));
    } catch (err) {
      console.warn('Unable to persist wishlist to storage', err);
    }

    // Sync wishlist with Shopify backend cart/customer attributes for permanent cross-session storage
    try {
      if (window.fetch) {
        const payload = {
          attributes: {
            wishlist_items: JSON.stringify(wishlistItems.map(i => ({ id: i.id, title: i.title, price: i.price }))),
            wishlist_count: wishlistItems.length
          }
        };
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }).catch(() => {});
      }
    } catch (e) {}
  }

  function updateWishlistBadges() {
    const badges = document.querySelectorAll('.wishlist-count-badge');
    badges.forEach(b => b.textContent = wishlistItems.length);
  }

  function syncButtonStates() {
    const allWishlistBtns = document.querySelectorAll('.wishlist-btn');
    allWishlistBtns.forEach(btn => {
      const id = String(btn.dataset.id || '');
      const isSaved = wishlistItems.some(item => String(item.id) === id);
      if (id && isSaved) {
        btn.classList.add('active', 'is-active');
        btn.setAttribute('title', 'Remove from Wishlist');
        btn.setAttribute('aria-label', 'Remove from Wishlist');
      } else {
        btn.classList.remove('active', 'is-active');
        btn.setAttribute('title', 'Add to Wishlist');
        btn.setAttribute('aria-label', 'Add to Wishlist');
      }
    });
  }

  // Render Full Wishlist Page (if /pages/wishlist is visited)
  function renderWishlistPage() {
    const pageGrid = document.getElementById('wishlistPageGrid');
    const pageEmpty = document.getElementById('wishlistPageEmpty');
    if (!pageGrid) return;

    if (wishlistItems.length === 0) {
      pageGrid.style.display = 'none';
      if (pageEmpty) pageEmpty.style.display = 'block';
      return;
    }

    pageGrid.style.display = 'grid';
    if (pageEmpty) pageEmpty.style.display = 'none';

    pageGrid.innerHTML = wishlistItems.map((item) => {
      const priceDisplay = item.priceFormatted || (item.price ? `₹${item.price}` : '₹799');
      const categoryDisplay = item.category || 'Handcrafted Bloom';
      const imgDisplay = item.image || '/assets/hero-crochet-bouquet.png';
      const urlDisplay = item.url || '/collections/all';

      return `
        <div class="wishlist-card product-card" data-product-id="${item.id}">
          <div class="wishlist-card-thumb product-thumb">
            <a href="${urlDisplay}">
              <img src="${imgDisplay}" alt="${escapeHtml(item.title)}" width="300" height="300" loading="lazy" decoding="async">
            </a>
            <button type="button" class="wishlist-card-heart wishlist-btn active is-active" data-id="${item.id}" data-title="${escapeHtml(item.title)}" title="Remove from Wishlist" aria-label="Remove from Wishlist">
              <svg class="wishlist-icon" width="16" height="16" viewBox="0 0 24 24" fill="#B72333" stroke="#B72333" stroke-width="2"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"></path></svg>
            </button>
          </div>
          <div class="wishlist-card-details product-details">
            <div>
              <span class="wishlist-card-cat product-category">${escapeHtml(categoryDisplay)}</span>
              <h3 class="wishlist-card-title product-title">
                <a href="${urlDisplay}">${escapeHtml(item.title)}</a>
              </h3>
              <div class="wishlist-card-price product-price">${priceDisplay}</div>
            </div>
            <button type="button" class="wishlist-card-add-btn wishlist-item-add-btn" data-id="${item.id}" data-title="${escapeHtml(item.title)}" data-price="${item.price || 799}" data-image="${imgDisplay}">
              MOVE TO BAG
            </button>
          </div>
        </div>
      `;
    }).join('');
  }

  // Handle Item Removal from Wishlist
  document.body.addEventListener('click', (e) => {
    const removeBtn = e.target.closest('.wishlist-item-remove-btn');
    if (removeBtn) {
      e.preventDefault();
      const id = String(removeBtn.dataset.id || '');
      wishlistItems = wishlistItems.filter(item => String(item.id) !== id);
      saveWishlistToStorage();
      updateWishlistBadges();
      syncButtonStates();
      renderWishlistPage();
      showWishlistNotification('Removed from wishlist', false);
    }
  });

  // Handle Move to Bag from Wishlist
  document.body.addEventListener('click', (e) => {
    const addBtn = e.target.closest('.wishlist-item-add-btn');
    if (addBtn) {
      e.preventDefault();
      const id = parseInt(addBtn.dataset.id || "1", 10);
      const title = addBtn.dataset.title || "Handcrafted Crochet Bloom";
      const price = parseInt(addBtn.dataset.price || "799", 10);
      const image = addBtn.dataset.image || '';

      addToCart({ id, title, price, image, quantity: 1, variant: "Handmade Standard" });
      if (window.CartDrawer) {
        window.CartDrawer.open();
      }
    }
  });

  // Handle Wishlist Heart Toggle Clicks on Product Cards & Product Pages
  document.body.addEventListener('click', (e) => {
    const btn = e.target.closest('.wishlist-btn');
    if (btn) {
      e.preventDefault();
      e.stopPropagation();

      const id = String(btn.dataset.id || '');
      const title = btn.dataset.title || btn.closest('.product-card')?.querySelector('.product-title')?.textContent?.trim() || 'Handcrafted Bloom';
      const priceVal = btn.dataset.price ? parseFloat(btn.dataset.price) : 799;
      const priceFormatted = btn.dataset.priceFormatted || `₹${Math.round(priceVal)}`;
      const cardImg = btn.dataset.image || btn.closest('.product-card')?.querySelector('.primary-image')?.src || document.querySelector('.main-image-display')?.src || '';
      const url = btn.dataset.url || btn.closest('.product-card')?.querySelector('a')?.href || window.location.href;
      const category = btn.dataset.category || btn.closest('.product-card')?.querySelector('.product-category')?.textContent?.trim() || 'Handcrafted Bloom';

      if (!id) return;

      const existingIdx = wishlistItems.findIndex(item => String(item.id) === id);
      if (existingIdx > -1) {
        // Remove
        wishlistItems.splice(existingIdx, 1);
        showWishlistNotification('Removed from wishlist', false);
      } else {
        // Add
        wishlistItems.push({
          id,
          title,
          price: priceVal,
          priceFormatted,
          image: cardImg,
          url,
          category
        });
        showWishlistNotification('Added to wishlist ❤️', true);
      }

      saveWishlistToStorage();
      updateWishlistBadges();
      syncButtonStates();
      renderWishlistPage();
    }
  });

  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function showWishlistNotification(msg, isAdded) {
    let toast = document.getElementById('agantukWishlistToast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'agantukWishlistToast';
      toast.style.cssText = `
        position: fixed;
        bottom: 24px;
        left: 50%;
        transform: translateX(-50%) translateY(80px);
        background: rgba(17, 17, 17, 0.94);
        color: #FFFFFF;
        padding: 9px 20px;
        border-radius: 999px;
        font-family: 'Poppins', sans-serif;
        font-size: 11.5px;
        font-weight: 500;
        letter-spacing: 0.3px;
        box-shadow: 0 6px 20px rgba(0,0,0,0.22);
        z-index: 9999999;
        transition: transform 0.28s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.28s ease;
        opacity: 0;
        pointer-events: none;
        display: flex;
        align-items: center;
        gap: 6px;
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
      `;
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.style.opacity = '1';
    toast.style.transform = 'translateX(-50%) translateY(0)';

    clearTimeout(toast.hideTimeout);
    toast.hideTimeout = setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateX(-50%) translateY(80px)';
    }, 2200);
  }

  // Initial Sync & Page Render
  updateWishlistBadges();
  syncButtonStates();
  renderWishlistPage();
}

// Interactive Custom Bouquet Builder Logic
function initBouquetBuilder() {
  const flowerItems = {
    tulip: { name: 'Pastel Crochet Tulip', price: 199, qty: 2 },
    rose: { name: 'Velvet Crochet Rose', price: 249, qty: 2 },
    sunflower: { name: 'Golden Sunflower', price: 299, qty: 1 },
    lavender: { name: 'Pure Lavender Stem', price: 149, qty: 3 },
    daisy: { name: 'Pastel Daisy Bloom', price: 179, qty: 0 },
    babysbreath: { name: "Baby's Breath Stem", price: 129, qty: 0 }
  };

  const wrapPrice = 150; // Ribbon & Wrap

  function updateBouquetSummary() {
    const listEl = document.getElementById('bouquetList');
    const totalEl = document.getElementById('bouquetTotal');
    if (!listEl || !totalEl) return;

    let subtotal = wrapPrice;
    let html = `<li><span>Artisan Jute & Ribbon Wrap</span> <span>₹${wrapPrice}</span></li>`;

    Object.keys(flowerItems).forEach(key => {
      const f = flowerItems[key];
      const card = document.getElementById(`card-${key}`);

      if (f.qty > 0) {
        const linePrice = f.price * f.qty;
        subtotal += linePrice;
        html += `<li><span>${f.qty}x ${f.name}</span> <span>₹${linePrice}</span></li>`;
        if (card) card.classList.add('selected');
      } else {
        if (card) card.classList.remove('selected');
      }
    });

    listEl.innerHTML = html;
    totalEl.textContent = `₹${subtotal.toLocaleString('en-IN')}`;
  }

  window.changeFlowerQty = function(key, delta) {
    if (!flowerItems[key]) return;
    flowerItems[key].qty = Math.max(0, flowerItems[key].qty + delta);
    const countEl = document.getElementById(`builder-qty-${key}`);
    if (countEl) countEl.textContent = flowerItems[key].qty;
    updateBouquetSummary();
  };

  const addBouquetBtn = document.getElementById('addCustomBouquetBtn');
  if (addBouquetBtn) {
    addBouquetBtn.addEventListener('click', () => {
      let totalFlowers = 0;
      let totalCost = wrapPrice;
      Object.keys(flowerItems).forEach(k => {
        totalFlowers += flowerItems[k].qty;
        totalCost += flowerItems[k].qty * flowerItems[k].price;
      });

      if (totalFlowers === 0) {
        alert('Please pick at least one crochet flower for your custom bouquet!');
        return;
      }

      const customBouquetImg = document.querySelector('.bouquet-display-img')?.src || document.querySelector('.hero-image-wrapper img')?.src || document.querySelector('.flower-picker-card img')?.src || '';
      addToCart({
        id: Date.now(),
        title: "Bespoke Custom Crochet Bouquet",
        price: totalCost,
        image: customBouquetImg,
        quantity: 1,
        variant: `${totalFlowers} Handpicked Stems + Artisan Wrap`
      });

      const cartOverlay = document.getElementById('cartOverlay');
      const cartDrawer = document.getElementById('cartDrawer');
      if (cartOverlay && cartDrawer) {
        cartOverlay.classList.add('open');
        cartDrawer.classList.add('open');
        renderCart();
      }
    });
  }

  updateBouquetSummary();
}

// Quick View Modal
function initQuickView() {
  const modal = document.getElementById('quickViewModal');
  const closeBtn = document.getElementById('closeQuickViewBtn');

  if (!modal) return;

  document.body.addEventListener('click', (e) => {
    if (e.target.closest('.quick-view-trigger')) {
      const btn = e.target.closest('.quick-view-trigger');
      const title = btn.dataset.title;
      const price = btn.dataset.price;
      const desc = btn.dataset.desc;
      const img = btn.dataset.image;

      document.getElementById('qvTitle').textContent = title;
      document.getElementById('qvPrice').textContent = `₹${price}`;
      document.getElementById('qvDesc').textContent = desc;
      document.getElementById('qvImage').src = img;

      modal.style.display = 'flex';
    }
  });

  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      modal.style.display = 'none';
    });
  }

  window.addEventListener('click', (e) => {
    if (e.target === modal) modal.style.display = 'none';
  });
}


// Dual 2-in-1 Carousel Controller for New Arrivals & Seasonal Drops
function initNewArrivalsCarousel() {
  const tabNew = document.getElementById('tabBtnNewArrivals');
  const tabSeasonal = document.getElementById('tabBtnSeasonalDrops');
  const trackNew = document.getElementById('trackNewArrivals');
  const trackSeasonal = document.getElementById('trackSeasonalDrops');
  const prevBtn = document.getElementById('dualPrevBtn');
  const nextBtn = document.getElementById('dualNextBtn');
  const viewAllBtn = document.getElementById('dualViewAllBtn');

  if (!tabNew || !tabSeasonal || !trackNew || !trackSeasonal) return;
  if (trackNew.dataset.themeInitialized === 'true') return;
  trackNew.dataset.themeInitialized = 'true';

  let activeTrack = trackNew;

  tabNew.addEventListener('click', function() {
    tabNew.style.opacity = '1';
    if (tabNew.querySelector('.tab-indicator')) tabNew.querySelector('.tab-indicator').style.opacity = '1';
    tabSeasonal.style.opacity = '0.4';
    if (tabSeasonal.querySelector('.tab-indicator')) tabSeasonal.querySelector('.tab-indicator').style.opacity = '0';
    trackNew.style.display = 'flex';
    trackSeasonal.style.display = 'none';
    activeTrack = trackNew;
    if (viewAllBtn) viewAllBtn.textContent = 'VIEW ALL NEW ARRIVALS';
  });

  tabSeasonal.addEventListener('click', function() {
    tabSeasonal.style.opacity = '1';
    if (tabSeasonal.querySelector('.tab-indicator')) tabSeasonal.querySelector('.tab-indicator').style.opacity = '1';
    tabNew.style.opacity = '0.4';
    if (tabNew.querySelector('.tab-indicator')) tabNew.querySelector('.tab-indicator').style.opacity = '0';
    trackSeasonal.style.display = 'flex';
    trackNew.style.display = 'none';
    activeTrack = trackSeasonal;
    if (viewAllBtn) viewAllBtn.textContent = 'VIEW ALL SEASONAL DROPS';
  });

  if (prevBtn) {
    prevBtn.addEventListener('click', function() {
      if (activeTrack) activeTrack.scrollBy({ left: -320, behavior: 'smooth' });
    });
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', function() {
      if (activeTrack) activeTrack.scrollBy({ left: 320, behavior: 'smooth' });
    });
  }
}

// Bestsellers Carousel Controller
function initBestsellersCarousel() {
  const track = document.getElementById('bestSellersTrack') || document.getElementById('bestsellersTrack');
  const prevBtn = document.getElementById('bestPrev');
  const nextBtn = document.getElementById('bestNext');
  if (!track || !prevBtn || !nextBtn) return;

  nextBtn.addEventListener('click', () => {
    track.scrollBy({ left: 320, behavior: 'smooth' });
  });

  prevBtn.addEventListener('click', () => {
    track.scrollBy({ left: -320, behavior: 'smooth' });
  });
}

// Smooth Scroll-Triggered Slide-In for Product Carousels
// 1. New Arrivals / Curated Drops (#new-arrivals)
// 2. Bestsellers (#featured-collection)
// 3. Flower Bouquets & Gift Boxes (#bouquets-baskets)
function initScrollDrivenCarousels() {
  const sectionIds = ['new-arrivals', 'featured-collection', 'bouquets-baskets'];
  const sections = sectionIds.map(id => document.getElementById(id)).filter(Boolean);

  if (sections.length === 0) return;

  sections.forEach(sec => sec.classList.add('carousel-slide-section'));

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in-view');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.08,
      rootMargin: '0px 0px -40px 0px'
    });

    sections.forEach(sec => observer.observe(sec));
  } else {
    sections.forEach(sec => sec.classList.add('is-in-view'));
  }

  // Trigger animation replay when switching tabs between New Arrivals and Curated Drops
  const tabNew = document.getElementById('tabBtnNewArrivals');
  const tabSeasonal = document.getElementById('tabBtnSeasonalDrops');
  const newArrivalsSec = document.getElementById('new-arrivals');

  function triggerTabAnimation() {
    if (!newArrivalsSec) return;
    newArrivalsSec.classList.remove('is-in-view');
    void newArrivalsSec.offsetWidth; // force reflow
    newArrivalsSec.classList.add('is-in-view');
  }

  if (tabNew) tabNew.addEventListener('click', () => setTimeout(triggerTabAnimation, 30));
  if (tabSeasonal) tabSeasonal.addEventListener('click', () => setTimeout(triggerTabAnimation, 30));
}

// Innovative Floral Center-Bloom Heading Animation for Bouquet Builder
function initFloralBloomHeadings() {
  const bloomHeaders = document.querySelectorAll('.builder-header-centered');
  if (bloomHeaders.length === 0) return;

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-bloomed');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -40px 0px'
    });

    bloomHeaders.forEach(el => observer.observe(el));
  } else {
    bloomHeaders.forEach(el => el.classList.add('is-bloomed'));
  }
}

// Lightweight Speculative Hover/Touch Instant-Prefetch for Near-Zero Latency Navigation
function initInstantPagePrefetch() {
  if (navigator.connection && (navigator.connection.saveData || /(2g)/.test(navigator.connection.effectiveType))) {
    return;
  }

  const prefetchedUrls = new Set();
  const currentOrigin = window.location.origin;

  function prefetchUrl(url) {
    if (!url || prefetchedUrls.has(url)) return;
    try {
      const parsed = new URL(url, currentOrigin);
      if (parsed.origin !== currentOrigin) return;
      if (parsed.pathname === window.location.pathname && parsed.search === window.location.search) return;
      if (/\.(jpg|jpeg|png|webp|gif|svg|mp4|css|js|json)$/i.test(parsed.pathname)) return;
      if (/checkout|cart|account/i.test(parsed.pathname)) return;

      prefetchedUrls.add(parsed.href);

      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = parsed.href;
      link.as = 'document';
      document.head.appendChild(link);
    } catch (e) {}
  }

  let hoverTimer = null;

  document.addEventListener('mouseover', (e) => {
    const anchor = e.target.closest('a[href]');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:')) return;

    clearTimeout(hoverTimer);
    hoverTimer = setTimeout(() => {
      prefetchUrl(href);
    }, 65);
  }, { passive: true });

  document.addEventListener('touchstart', (e) => {
    const anchor = e.target.closest('a[href]');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:')) return;
    prefetchUrl(href);
  }, { passive: true });
}
